import { useIonViewWillEnter } from '@ionic/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Permission,
  ProfileViewSource,
  type ProfileModuleDto,
} from '@up2stay/up2promo-api-client';
import { Alert, AlertDescription, AlertTitle } from '@up2stay/up2promo-ui';
import { Avatar, AvatarFallback, AvatarImage } from '@up2stay/up2promo-ui';
import { Badge } from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import { Card, CardContent } from '@up2stay/up2promo-ui';
import {
  brandAvatarGlowClass,
  brandGradFillClass,
  brandMarkTileClass,
  cn,
  successStatusDotClass,
} from '@up2stay/up2promo-ui';
import { Skeleton } from '@up2stay/up2promo-ui';
import { MapPin, Pencil, Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { shellPaths } from '@/core/router/app-paths';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { PROFILE_OVERVIEW_SPLIT_GRID_CLASS } from '@/core/components/layout';
import { PageHeader } from '@/core/components/page-header';
import { PhysicalCardsSection } from '@/core/components/profiles/physical-cards-section';
import {
  ProfilesPreviewPanel,
  type ProfilesPreviewPanelProps,
} from '@/core/components/profiles/profiles-preview-panel';
import { ProfilesSharePanel } from '@/core/components/profiles/profiles-share-panel';
import { ProfilesUsageCard } from '@/core/components/profiles/profiles-usage-card';
import { useGuidanceStore } from '@/core/guidance/guidance.store';
import { useAuthPublicConfig } from '@/core/hooks/use-auth-public-config';
import { useLocale } from '@/core/hooks/use-locale';
import { useLocations } from '@/core/hooks/use-locations';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { type CreateProfileInput, useProfile } from '@/core/hooks/use-profile';
import {
  type ProfileUsageRangeDaysValue,
  useProfileUsage,
} from '@/core/hooks/use-profile-usage';
import { useUserTeamMembership } from '@/core/hooks/use-user-team-membership';
import { useWalletCard } from '@/core/hooks/use-wallet-card';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';
import { LIST_PAGE_HARD_CAP } from '@/core/lib/list-pagination';
import {
  buildPreviewModules,
  omitTeamMembersModules,
} from '@/core/lib/profile-preview';
import { addProfileToWallet } from '@/core/lib/wallet-card';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useAuthStore } from '@/core/store/auth.store';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { getApiErrorMessage } from '@/core/utils/api-error.utils';
import { buildPublicProfileHashUrl } from '@/core/utils/product-link.utils';
import { buildProfileUrl } from '@/core/utils/profile.utils';
import { AppIonPage } from '@/core/components/app-ion-page';

function markGetStartedShareDone() {
  useGuidanceStore.getState().signalGetStartedShare();
}

export function ProfilesPage() {
  const locale = useLocale();
  const dict = useDictionary();
  const isMobile = useIsMobile();
  const { navigateShell } = useNavigateShell();
  const user = useAuthStore((state) => state.user);
  const currentWorkspace = useCurrentWorkspace();
  const { hasTeamMembership, isLoading: isTeamMembershipLoading } =
    useUserTeamMembership(currentWorkspace?.id);
  const queryClient = useQueryClient();
  const {
    profile,
    isLoading: profileLoading,
    createProfile,
    refetch: refetchProfile,
  } = useProfile();
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);
  const [isAddingToWallet, setIsAddingToWallet] = useState(false);
  const [usageRangeDays, setUsageRangeDays] =
    useState<ProfileUsageRangeDaysValue>(7);
  const { can } = usePermissions();
  const { walletEnabled } = useAuthPublicConfig();
  const canEditWorkspaceSettings = can(Permission.WORKSPACE_MANAGE_SETTINGS);
  const { locations } = useLocations(undefined, {
    limit: LIST_PAGE_HARD_CAP,
    queryKeySuffix: 'profile-preview',
  });
  const locationsById = useMemo(() => {
    const map = new Map<
      string,
      {
        name?: string | null;
        address?: string | null;
        coordinates?: {
          latitude?: number | null;
          longitude?: number | null;
        } | null;
      }
    >();
    for (const location of locations) {
      map.set(location.id, location);
    }
    return map;
  }, [locations]);

  const previewModules = useMemo(() => {
    const stored = profile?.modules as ProfileModuleDto[] | undefined;
    const modules =
      isTeamMembershipLoading || hasTeamMembership
        ? stored
        : omitTeamMembersModules(stored);
    return buildPreviewModules({
      modules,
      selfUserId: user?.id ?? null,
      selfUsername: user?.username ?? null,
      locationsById,
    });
  }, [
    profile?.modules,
    user?.id,
    user?.username,
    locationsById,
    hasTeamMembership,
    isTeamMembershipLoading,
  ]);
  const {
    state: walletCardState,
    refresh: refreshWalletCard,
    watchForChange: watchWalletCard,
  } = useWalletCard(profile?.id);
  const { data: profileUsage, isLoading: profileUsageLoading } =
    useProfileUsage(profile?.id, usageRangeDays);

  useIonViewWillEnter(() => {
    void refetchProfile();
    void queryClient.invalidateQueries({
      queryKey: ['physical-business-cards', currentWorkspace?.id],
    });
    refreshWalletCard();
  });

  const workspaceUsername = currentWorkspace?.username || null;
  const isWorkspaceProfile = Boolean(profile?.workspace);

  // Direct profile URL (for sharing - always use direct profile viewer URL)
  const directProfileUrl = useMemo(() => {
    return buildProfileUrl({
      profile,
      username: user?.username,
      locale,
      workspaceUsername: workspaceUsername,
    });
  }, [profile, user?.username, locale, workspaceUsername]);

  // QR code / wallet gate: only the stable publicHash URL (never direct profile URL).
  const profileLink = useMemo(() => {
    if (profile?.publicHash) {
      return buildPublicProfileHashUrl(
        profile.publicHash,
        ProfileViewSource.PROFILE_QR,
      );
    }
    return null;
  }, [profile]);

  const isLoading = profileLoading;

  const showNoLinkNotice =
    !isLoading &&
    profile &&
    !directProfileUrl &&
    (!user?.username || (isWorkspaceProfile && !workspaceUsername));
  const noLinkDict = (
    dict.profiles.share as { noLinkNotice?: Record<string, string> }
  )?.noLinkNotice;

  // Get user data or use fallback
  const userData = useMemo(() => {
    const defaultUser =
      (dict.profiles.share as { defaultUser?: string }).defaultUser || '';
    if (user) {
      return {
        name:
          `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
          user.email ||
          defaultUser,
        email: user.email || '',
        avatar: user.photo?.path
          ? getAbsoluteFileUrl(user.photo.path)
          : undefined,
      };
    }
    return {
      name: defaultUser,
      email: '',
      avatar: undefined,
    };
  }, [user, dict.profiles.share]);

  // Get initials for avatar fallback
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Bild-URL für die Card: wie in Vorschau/Switcher zuerst Profilbild, sonst User-Photo
  const profileImagePath = profile?.profileImage
    ? (profile.profileImage as { path?: string }).path
    : undefined;
  const profileImageUrl =
    profileImagePath && profileImagePath.trim() !== ''
      ? getAbsoluteFileUrl(profileImagePath)
      : undefined;
  const cardAvatarUrl = profileImageUrl || userData.avatar;
  const showProfileImageInCard = Boolean(cardAvatarUrl);

  // Get display name (from profile title or user name)
  const displayName = useMemo(() => {
    if (profile?.title) {
      return profile.title;
    }
    return userData.name;
  }, [profile, userData.name]);

  // Get job title and company (handover uses · between role and company)
  const jobInfo = useMemo(() => {
    if (profile?.jobTitle && profile?.company) {
      return `${profile.jobTitle} · ${profile.company}`;
    }
    return profile?.jobTitle || profile?.company || null;
  }, [profile]);

  // Get location
  const location = profile?.location || null;

  const editProfileLabel = isMobile
    ? ((dict.profiles as { editProfileShort?: string }).editProfileShort ??
      dict.profiles.editProfile)
    : dict.profiles.editProfile;

  const createProfileErrorFallback =
    dict?.profiles?.createProfileFailed ??
    'Could not create your profile. Please try again.';

  const handleCreateProfile = useCallback(async () => {
    if (!user || !currentWorkspace?.id) return;
    setIsCreatingProfile(true);
    try {
      const userTitle =
        `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
        user.email ||
        'User';
      const createData: CreateProfileInput = {
        title: userTitle,
        visibility: 'unlisted',
        language: 'auto',
        workspaceId: currentWorkspace.id,
        modules: [],
      };
      await createProfile(createData);
      navigateShell(shellPaths.profilesEdit);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, createProfileErrorFallback));
    } finally {
      setIsCreatingProfile(false);
    }
  }, [
    user,
    currentWorkspace,
    createProfile,
    navigateShell,
    createProfileErrorFallback,
  ]);

  const handleShareViaEmail = () => {
    // Always use direct profile URL for sharing (not the product URL)
    if (!directProfileUrl) return;
    markGetStartedShareDone();

    const shareDict = dict.profiles.share as {
      emailSubject?: string;
      emailBody?: string;
      smsBody?: string;
      noUsernameHint?: string;
      usernameRequired?: string;
      defaultUser?: string;
    };
    // Encode each dynamic part separately to prevent malformed URLs
    const subjectText =
      profile?.title || displayName || shareDict.emailSubject || '';
    const bodyText = shareDict.emailBody || '';
    // Encode each component separately to ensure proper URL encoding
    const encodedSubject = encodeURIComponent(subjectText);
    const encodedBodyText = encodeURIComponent(bodyText);
    const encodedProfileUrl = encodeURIComponent(directProfileUrl);
    // Construct body with encoded components
    const encodedBody = `${encodedBodyText}%0A%0A${encodedProfileUrl}`;

    window.location.href = `mailto:?subject=${encodedSubject}&body=${encodedBody}`;
  };

  const handleShareViaSms = () => {
    // Always use direct profile URL for sharing (not the product URL)
    if (!directProfileUrl) return;
    markGetStartedShareDone();

    const shareDict = dict.profiles.share as {
      emailSubject?: string;
      emailBody?: string;
      smsBody?: string;
      noUsernameHint?: string;
      usernameRequired?: string;
      defaultUser?: string;
    };
    // Encode each dynamic part separately to prevent malformed URLs
    const smsBodyText = shareDict.smsBody || '';
    // Encode each component separately to ensure proper URL encoding
    const encodedSmsBody = encodeURIComponent(smsBodyText);
    const encodedProfileUrl = encodeURIComponent(directProfileUrl);
    // Construct message with encoded components (space encoded as %20)
    const encodedMessage = `${encodedSmsBody}%20${encodedProfileUrl}`;

    window.location.href = `sms:?body=${encodedMessage}`;
  };

  const handleAddToWallet = useCallback(async () => {
    if (!profile || !profileLink || isAddingToWallet) return;

    const shareDict = dict.profiles.share as {
      defaultProfileName?: string;
      walletAddFailed?: string;
    };

    setIsAddingToWallet(true);
    try {
      await addProfileToWallet({
        profileId: profile.id,
        fileName: `${profile.title || shareDict.defaultProfileName || 'profile'}.pkpass`,
      });
      // The device registers itself with Apple only after the user confirms,
      // so keep polling until that registration shows up.
      watchWalletCard();
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, shareDict.walletAddFailed || '') ||
          shareDict.walletAddFailed ||
          '',
      );
    } finally {
      setIsAddingToWallet(false);
    }
  }, [
    profile,
    profileLink,
    isAddingToWallet,
    dict.profiles.share,
    watchWalletCard,
  ]);

  const walletButton = useMemo(() => {
    const shareDict = dict.profiles.share as {
      addToWallet?: string;
      walletUpdate?: string;
      walletUpToDate?: string;
      walletOutdatedHint?: string;
    };

    if (walletCardState === 'outdated') {
      return {
        label: shareDict.walletUpdate || '',
        hint: shareDict.walletOutdatedHint || '',
      };
    }
    if (walletCardState === 'current') {
      return { label: shareDict.walletUpToDate || '', hint: null };
    }
    return { label: shareDict.addToWallet || '', hint: null };
  }, [walletCardState, dict.profiles.share]);

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout className="min-w-0">
          <PageHeader
            title={dict.profiles.title}
            breadcrumb={
              <>
                {dict.nav.navDomainWork}
                <span className="mx-1.5 text-muted-foreground/70">/</span>
                {dict.nav.profiles}
              </>
            }
            actionsLayout="inline"
            actions={
              profile ? (
                <Button
                  size={isMobile ? 'md' : 'lg'}
                  className={cn(
                    'shrink-0',
                    isMobile ? 'min-h-10 px-3.5' : 'min-h-11',
                  )}
                  onClick={() => navigateShell(shellPaths.profilesEdit)}
                >
                  <Pencil
                    className={cn('h-4 w-4', isMobile ? 'mr-1.5' : 'mr-2')}
                  />
                  {editProfileLabel}
                </Button>
              ) : (
                <Button
                  size={isMobile ? 'md' : 'lg'}
                  className={cn(
                    'shrink-0',
                    isMobile ? 'min-h-10 px-3.5' : 'min-h-11',
                  )}
                  disabled={isCreatingProfile}
                  onClick={() => void handleCreateProfile()}
                >
                  <Plus
                    className={cn('h-4 w-4', isMobile ? 'mr-1.5' : 'mr-2')}
                  />
                  {dict.profiles.createProfile}
                </Button>
              )
            }
          />
          {/* Show empty state if no profile exists */}
          {!isLoading && !profile ? (
            <Card className="flex flex-1 flex-col">
              <CardContent className="flex flex-1 flex-col items-center justify-center gap-4 p-8 sm:p-12">
                <div className="flex max-w-md flex-col items-center gap-4 text-center">
                  <div
                    className={cn(
                      'flex h-16 w-16 items-center justify-center rounded-2xl',
                      brandMarkTileClass,
                    )}
                  >
                    <Plus className="h-8 w-8" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <h3 className="text-[15.5px] font-bold leading-tight text-foreground">
                      {dict.profiles.noProfileYet}
                    </h3>
                    <p className="text-[13.5px] leading-relaxed text-muted-foreground">
                      {dict.profiles.descriptionCreate}
                    </p>
                  </div>
                  <Button
                    size="lg"
                    className="min-h-11 gap-2"
                    data-guidance-target="profiles-empty"
                    disabled={isCreatingProfile}
                    onClick={() => void handleCreateProfile()}
                  >
                    <Plus className="h-4 w-4" />
                    {dict.profiles.createProfile}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            /* Content + preview; stacks below xl so the identity column is not
               squeezed beside the phone mockup while the shell sidebar may
               reserve space (DEV-437 / shell overlay @ 1280). */
            <div className={PROFILE_OVERVIEW_SPLIT_GRID_CLASS}>
              <div className="flex min-w-0 flex-col gap-4 overflow-x-hidden md:gap-5">
                {showNoLinkNotice && noLinkDict && (
                  <Alert
                    variant="default"
                    className="border-amber-500/50 bg-amber-500/5"
                  >
                    <AlertTitle className="text-amber-800 dark:text-amber-200">
                      {noLinkDict.title}
                    </AlertTitle>
                    <AlertDescription>
                      <div className="mt-2 space-y-2 text-sm text-muted-foreground">
                        {!user?.username && (
                          <p>{noLinkDict.userUsernameMissing}</p>
                        )}
                        {isWorkspaceProfile && !workspaceUsername && (
                          <p>
                            {canEditWorkspaceSettings
                              ? noLinkDict.workspaceUsernameMissing
                              : noLinkDict.workspaceUsernameMissingNoPermission}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2 pt-1">
                          {!user?.username && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                navigateShell(shellPaths.moreUserSettings)
                              }
                            >
                              {noLinkDict.goToUserSettings}
                            </Button>
                          )}
                          {isWorkspaceProfile &&
                            !workspaceUsername &&
                            canEditWorkspaceSettings && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  navigateShell(
                                    shellPaths.moreWorkspaceSettings,
                                  )
                                }
                              >
                                {noLinkDict.goToWorkspaceSettings}
                              </Button>
                            )}
                        </div>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
                <Card>
                  <CardContent
                    className={cn(isMobile ? 'p-[18px]' : 'px-6 py-[22px]')}
                  >
                    {isLoading && !profile ? (
                      <div className="flex items-center gap-4">
                        <Skeleton
                          className={cn(
                            'shrink-0',
                            isMobile
                              ? 'size-[58px] rounded-2xl'
                              : 'size-[60px] rounded-[14px]',
                          )}
                        />
                        <div className="min-w-0 flex-1 space-y-2">
                          <Skeleton className="h-7 w-48" />
                          <Skeleton className="h-4 w-64" />
                          <Skeleton className="h-4 w-40" />
                        </div>
                      </div>
                    ) : (
                      <div
                        className={cn(
                          'flex items-center',
                          isMobile ? 'gap-[15px]' : 'gap-[18px]',
                        )}
                      >
                        <Avatar
                          className={cn(
                            'shrink-0',
                            brandAvatarGlowClass,
                            isMobile
                              ? 'size-[58px] rounded-2xl'
                              : 'size-[60px] rounded-[14px]',
                          )}
                        >
                          {showProfileImageInCard ? (
                            <AvatarImage
                              src={cardAvatarUrl}
                              alt={displayName}
                            />
                          ) : null}
                          <AvatarFallback
                            className={cn(
                              brandGradFillClass,
                              isMobile
                                ? 'rounded-2xl text-xl'
                                : 'rounded-[14px] text-[21px]',
                            )}
                          >
                            {getInitials(displayName)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h2
                              className={cn(
                                'break-words font-bold tracking-[-0.3px] text-foreground',
                                isMobile ? 'text-[18px]' : 'text-[19px]',
                              )}
                            >
                              {displayName}
                            </h2>
                            {profileLink ? (
                              <Badge
                                variant="success"
                                className={cn(
                                  'shrink-0 font-semibold',
                                  isMobile
                                    ? 'gap-1 px-2 py-0.5 text-[10.5px]'
                                    : 'gap-1.5 px-3 py-1.5 text-xs',
                                )}
                              >
                                <span
                                  className={cn(
                                    successStatusDotClass,
                                    isMobile ? 'size-1.5' : 'size-[7px]',
                                  )}
                                />
                                {(dict.profiles as { liveStatus?: string })
                                  .liveStatus ?? 'Live'}
                              </Badge>
                            ) : null}
                          </div>
                          {jobInfo ? (
                            <p
                              className={cn(
                                'break-words text-[13px] text-muted-foreground',
                                isMobile ? 'mt-1' : 'mt-[3px]',
                              )}
                            >
                              {jobInfo}
                            </p>
                          ) : null}
                          {location ? (
                            isMobile ? (
                              <p className="mt-0.5 flex items-center gap-1 text-[12.5px] text-muted-foreground/80">
                                <MapPin className="size-[13px] shrink-0" />
                                {location}
                              </p>
                            ) : (
                              <p className="mt-1 break-words text-[13px] text-muted-foreground">
                                {location}
                              </p>
                            )
                          ) : null}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <PhysicalCardsSection
                  dict={
                    (
                      dict.profiles as {
                        physicalCards?: Record<string, string | undefined>;
                      }
                    ).physicalCards
                  }
                  enabled={Boolean(profile)}
                />

                <ProfilesSharePanel
                  dict={dict}
                  isLoading={isLoading}
                  profile={profile}
                  profileLink={profileLink}
                  directProfileUrl={directProfileUrl}
                  userUsername={user?.username}
                  walletEnabled={walletEnabled}
                  walletCardState={walletCardState}
                  walletButton={walletButton}
                  isAddingToWallet={isAddingToWallet}
                  onShareCopied={markGetStartedShareDone}
                  onShareViaSms={handleShareViaSms}
                  onShareViaEmail={handleShareViaEmail}
                  onAddToWallet={handleAddToWallet}
                />

                {profile ? (
                  <ProfilesUsageCard
                    dict={dict.profiles.usage}
                    usage={profileUsage}
                    isLoading={profileUsageLoading}
                    rangeDays={usageRangeDays}
                    onRangeChange={setUsageRangeDays}
                    locale={locale}
                    copyLinkUrl={directProfileUrl || null}
                    walletEnabled={walletEnabled}
                    walletReady={Boolean(profileLink)}
                    isAddingToWallet={isAddingToWallet}
                    onAddToWallet={handleAddToWallet}
                    onCopyLink={markGetStartedShareDone}
                  />
                ) : null}
              </div>

              <ProfilesPreviewPanel
                isLoading={isLoading}
                profile={profile as ProfilesPreviewPanelProps['profile']}
                user={user ?? null}
                userData={userData}
                workspaceUsername={workspaceUsername}
                currentWorkspace={currentWorkspace}
                previewModules={previewModules ?? []}
              />
            </div>
          )}
        </AppPageLayout>
      </AppPageContent>
    </AppIonPage>
  );
}
